/**
 * Supported Intelligence Insights module actions.
 * Unsupported actions are filtered out at render time instead of showing toast placeholders.
 */

export const INSIGHTS_HIGHLIGHTS_NAV_KEYS = {
    open_key_findings: 'key_findings',
    open_risks_signals: 'risks_signals',
    open_evidence_follow_ups: 'evidence_follow_ups',
    open_management_summary: 'management_summary',
};

export const INSIGHTS_HIGHLIGHTS_ROUTES = {
    open_key_findings: '/observations',
    open_risks_signals: '/raid',
    open_evidence_follow_ups: '/irl',
    open_irl: '/irl',
    open_raid_item: '/raid',
    open_observation: '/observations',
};

const COMMON_SUPPORTED = new Set([
    'view_details',
    'view_evidence',
    'open_evidence_panel',
    'create_irl',
    'add_to_raid',
    'add_raid',
    'create_observation',
    'save_to_journal',
]);

const UNSUPPORTED_ACTION_KEYS = new Set([
    'export_evidence',
    'create_report_draft',
    'export_scope',
]);

const MODULE_SUPPORTED_KEYS = {
    management_summary: new Set([
        ...COMMON_SUPPORTED,
        'open_key_findings',
        'open_risks_signals',
        'open_evidence_follow_ups',
    ]),
    key_findings: new Set([
        ...COMMON_SUPPORTED,
        'open_risks_signals',
        'open_evidence_follow_ups',
    ]),
    swot_analysis: new Set([
        ...COMMON_SUPPORTED,
        'open_risks_signals',
        'open_key_findings',
        'open_evidence_follow_ups',
    ]),
    risks_signals: new Set([...COMMON_SUPPORTED, 'open_raid_item', 'open_raid']),
    evidence_follow_ups: new Set([
        ...COMMON_SUPPORTED,
        'open_raid_item',
        'open_raid',
        'review_sources',
        'review_failed',
        'generate_analysis',
        'generate_new_version',
    ]),
    investment_opinion: new Set([
        ...COMMON_SUPPORTED,
        'view_rationale',
        'view_relevance',
        'view_consequence',
        'open_evidence_follow_ups',
    ]),
    remediation_investment: new Set([
        ...COMMON_SUPPORTED,
        'review_estimate',
        'create_task',
        'open_key_findings',
        'open_risks_signals',
    ]),
};

export function resolveInsightsActionKey(action) {
    if (!action) return null;
    return action.key || action.actionType || action.action_type || null;
}

/** Actions allowed while viewing a historical (read-only) analysis snapshot. */
const READ_ONLY_NAVIGATION_KEYS = new Set([
    ...Object.keys(INSIGHTS_HIGHLIGHTS_NAV_KEYS),
    'open_management_summary',
    'view_details',
    'view_evidence',
    'open_evidence_panel',
    'view_rationale',
    'view_relevance',
    'view_consequence',
]);

export function isInsightsReadOnlyNavigationAction(action) {
    const key = resolveInsightsActionKey(action);
    return Boolean(key && READ_ONLY_NAVIGATION_KEYS.has(key));
}

export function isInsightsReadOnlyBlocked(readOnly, action) {
    return Boolean(readOnly && action && !isInsightsReadOnlyNavigationAction(action));
}

const SWOT_EVIDENCE_OBJECT_TYPES = new Set([
    'finding',
    'evidence_matter',
    'risk',
    'theme',
    'contradiction',
]);

export function resolveSwotEvidenceTarget(item, action = {}) {
    if (!item) return null;
    const target = action.target || {};
    const objectType = target.object_type || target.type;
    const objectId = target.object_id || target.id;
    if (
        objectType &&
        objectId &&
        SWOT_EVIDENCE_OBJECT_TYPES.has(String(objectType))
    ) {
        return { objectType: String(objectType), objectId: String(objectId) };
    }
    if (item.relatedFindingIds?.[0]) {
        return { objectType: 'finding', objectId: String(item.relatedFindingIds[0]) };
    }
    if (item.relatedRiskIds?.[0]) {
        return { objectType: 'risk', objectId: String(item.relatedRiskIds[0]) };
    }
    if (item.relatedThemeIds?.[0]) {
        return { objectType: 'theme', objectId: String(item.relatedThemeIds[0]) };
    }
    if (item.relatedEvidenceMatterIds?.[0]) {
        return {
            objectType: 'evidence_matter',
            objectId: String(item.relatedEvidenceMatterIds[0]),
        };
    }
    return null;
}

export function canResolveSwotEvidence(item, action = {}) {
    return Boolean(resolveSwotEvidenceTarget(item, action));
}

export function isInsightsActionSupported(action, options = {}) {
    const key = resolveInsightsActionKey(action);
    if (!key || UNSUPPORTED_ACTION_KEYS.has(key)) return false;

    const { moduleKey, context = {}, handlers = {} } = options;

    if (moduleKey === 'highlights') {
        if (COMMON_SUPPORTED.has(key)) return true;
        if (INSIGHTS_HIGHLIGHTS_NAV_KEYS[key]) return Boolean(handlers.onOpenModule);
        if (INSIGHTS_HIGHLIGHTS_ROUTES[key] || action?.route) return true;
        return false;
    }

    const supported = MODULE_SUPPORTED_KEYS[moduleKey];
    if (!supported?.has(key)) return false;

    if (key === 'review_sources' || key === 'review_failed') {
        return Boolean(handlers.onReviewFailedSource);
    }
    if (key === 'generate_analysis' || key === 'generate_new_version') {
        return Boolean(handlers.onGenerateAnalysis);
    }
    if (
        (key === 'view_evidence' || key === 'open_evidence_panel') &&
        moduleKey === 'swot_analysis' &&
        context.item
    ) {
        return canResolveSwotEvidence(context.item, action);
    }
    if (
        (key === 'view_relevance' || key === 'view_consequence') &&
        moduleKey === 'investment_opinion' &&
        context.item
    ) {
        return hasDistinctFactorExpandDetail(context.item, key);
    }

    return true;
}

export function factorExpandDetail(factor, expandKey) {
    if (expandKey === 'view_relevance') {
        return factor?.relevance || factor?.investmentRelevance || null;
    }
    if (expandKey === 'view_consequence') {
        return factor?.consequence || factor?.potentialConsequence || null;
    }
    return factor?.consequence || factor?.relevance || null;
}

export function hasDistinctFactorExpandDetail(factor, expandKey) {
    const detail = factorExpandDetail(factor, expandKey);
    if (!detail) return false;
    const body = String(factor?.body || '').trim();
    return detail.trim() !== body;
}

export function factorExpandLabel(expandKey) {
    if (expandKey === 'view_relevance') return 'Investment relevance';
    if (expandKey === 'view_consequence') return 'Potential consequence';
    return null;
}

export function filterSupportedInsightsActions(actions, options = {}) {
    return (actions || []).filter(action => isInsightsActionSupported(action, options));
}

export function firstSupportedInsightsAction(actions, options = {}) {
    return filterSupportedInsightsActions(actions, options)[0] || null;
}
